// M4 world §15 — oil sands & extra-heavy oil: loops that are not wells-and-decline.
//   Mining (field.oilSands.extraction 'mining'): no wells. Each leased plot opened as a mine block holds
//     OOIP × rf (0.85) / plots of bitumen. mine_fleet (200k t ore/d, heavy_equipment_op crews) → hot_water_extraction
//     (bitumen = ore / oreTonnesPerBbl × recovery, capped by plant capacity) → bitumen_upgrader (syncrude, yield 0.85,
//     priced like WTI) or paraffinic_froth_treatment (2013+) + diluent_supply (dilbit at WCS). Bitumen with no outlet is
//     left in the ground. Tailings (tailingsPerBbl) need a tailings_facility; the reclamation liability grows with
//     output; Directive 074 (2009) fines 1%/month of the untreated liability until you pay to treat it.
//   SAGD ('sagd'): wells (slant rig) are well pairs. An otsg_steam_plant must supply steam = SOR × bitumen
//     (2.6, × 0.75 solvent retrofit, × 0.85 sagd_optimization); steam burns 0.4 Mcf/bbl at AECO. Each pair follows
//     rules.sagdRate (270-d ramp, 8-yr plateau, 12%/yr decline) from first steam. Bitumen is sold as dilbit.
//   Orinoco extra-heavy (field.heavyOil): cold production; sales need diluent (30% blend) or an upgrader.
// Diluent rides along and is sold back at the blend price: its net cost is blend/(1 − blend) × (diluent − realized).
import { wellsOnField } from '../indexes.js';
import { eqDef, fieldDef, stat, yf, unitPrice, isAvailable, eraDollars } from '../defs.js';
import { OIL_SANDS, HEAVY_OIL, sagdRate, wtiRatio, hubSpread, gasHubPrice, MMBTU_PER_MCF, lerpAt, carbonPrice, CO2_T_PER_BOE } from '../../data/m4/rules.js';
import { inflate } from '../../data/cpi.js';
import { reservoirsAt } from '../geo.js';
import { spotPrice } from '../../economy/market.js';
import { addItem } from '../../core/state.js';
import { storeOil } from '../production.js';
import { hasUpgrade } from '../research.js';
import { regionBlock } from '../m2/politics.js';
import { spend, notify, fmt$ } from '../util.js';
import { W, peekW, m4On, itemsAt, ownWell } from './worldCore.js';
import { wildfireShut } from './worldPolitics.js';

export const D074_FINE_PER_MONTH = 0.01;    // share of the untreated tailings liability (est.)
export const CREWS_PER_FLEET = 40;
export const SAGD_PIPELINE_TARIFF = 3;      // $/bbl to Hardisty (2015 $, est.)
export const UPGRADER_PRICE_UPLIFT = 1.12;  // Orinoco upgraded crude vs Merey (est.)

const osDef = (fid) => fieldDef(fid)?.oilSands ?? null;
export const isMining = (fid) => osDef(fid)?.extraction === 'mining';
export const isSagd = (fid) => osDef(fid)?.extraction === 'sagd';
export const isHeavy = (fid) => !!fieldDef(fid)?.heavyOil;
const unitsAt = (state, fid, kind) => itemsAt(state, fid, (it, d) => d?.cat === 'oil_sands' && stat(d, 'kind', '') === kind);
const sumStat = (items, key) => items.reduce((a, it) => a + stat(eqDef(it.defId), key, 0), 0);
export function osState(state, fid) {
  const o = (W(state).oilSands[fid] ??= { blocks: {}, tailingsBbl: 0, liability: 0, treated: 0, fines: 0, bitumenBbl: 0, syncrudeBbl: 0, dilbitBbl: 0, syncShare: 0, status: null, shutUntil: 0, today: null });
  return o;
}

// ---- prices (pure) ------------------------------------------------------------------------------------------------------
export function oilSandsPrices(state, y = Math.min(yf(state), 2025.9)) {
  const brent = Math.max(0, spotPrice(state));
  const wti = brent * wtiRatio(y);
  const wcs = wti + hubSpread('wcs', y);
  const diluent = wti + OIL_SANDS.blending.diluentPremiumOverWti;
  const aeco = Math.max(0.2, gasHubPrice('aeco', y)) * MMBTU_PER_MCF; // $/Mcf
  return { brent, wti, wcs, diluent, aeco, merey: brent * (1 - HEAVY_OIL.merey16Discount) };
}
export const diluentNetCost = (blend, diluent, realized) => blend / (1 - blend) * Math.max(0, diluent - realized);

// Unit economics of one SAGD pair ($/bbl bitumen): cash and full-cycle (pair + share of the central plant over the
// plateau). Pure; acceptance: SOR 3, gas $8, WCS $40 loses money.
export function sagdUnitEconomics({ sor = OIL_SANDS.sagd.sorTypical, gasPerMcf, wcs, wti = null, year = 2015, peakBbld = 800, solvent = false, optimized = false } = {}) {
  const S = OIL_SANDS.sagd;
  const sorEff = sor * (solvent ? 0.75 : 1) * (optimized ? 0.85 : 1);
  const gasMcf = sorEff * S.gasMcfPerBblSteam;
  const gas = gasMcf * gasPerMcf;
  const plant = eqDef('otsg_steam_plant');
  const opex = inflate(stat(plant, 'opexPerBbl', 8), stat(plant, 'baseYear', 2010), Math.min(year, 2025));
  const w = wti ?? wcs + 14;
  const diluent = diluentNetCost(OIL_SANDS.blending.dilbitDiluentFrac, w + OIL_SANDS.blending.diluentPremiumOverWti, wcs);
  const transport = inflate(SAGD_PIPELINE_TARIFF, 2015, Math.min(year, 2025));
  const royalty = wcs * 0.01;
  const carbon = CO2_T_PER_BOE.oil_sands_sagd * carbonPrice('alberta_tier', Math.min(year, 2025)); // TIER (B charges it in play)
  const cash = wcs - gas - opex - diluent - transport - royalty - carbon;
  const life = S.rampDays / 365.25 / 2 + S.plateauYears; // payback over the plateau (est.)
  const pairCost = lerpAt(S.pairCostM, year) * 1e6;
  const plantPerBbld = inflate(plant?.basePrice ?? 1e9, plant?.year ?? 2001, Math.min(year, 2025)) / Math.max(1, stat(plant, 'capacityBbld', 30000));
  const capexPerBbl = (pairCost + plantPerBbld * peakBbld) / (peakBbld * 365.25 * life);
  return { sor: sorEff, gasMcfPerBbl: gasMcf, gas, opex, diluent, transport, royalty, carbon, cash, capexPerBbl, fullCycle: cash - capexPerBbl };
}

// ---- actions -------------------------------------------------------------------------------------------------------------
export function buildOilSandsUnitImpl(state, fieldId, defId) {
  const def = eqDef(defId);
  const fdef = fieldDef(fieldId);
  if (!def || def.cat !== 'oil_sands') return { ok: false, msg: 'Not an oil-sands unit.' };
  if (!fdef || !state.fields[fieldId]) return { ok: false, msg: 'No such field.' };
  if (!m4On(state)) return { ok: false, msg: 'Not before 2000.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} is not available until ${def.year}.` };
  const kind = stat(def, 'kind', '');
  const okField = fdef.oilSands || (isHeavy(fieldId) && ['upgrader', 'diluent'].includes(kind));
  if (!okField) return { ok: false, msg: `${fdef.name} has no oil sands or extra-heavy oil.` };
  if (isSagd(fieldId) && ['mine_fleet', 'extraction', 'froth'].includes(kind)) return { ok: false, msg: `${fdef.name} is too deep to mine: drill SAGD well pairs.` };
  if (isMining(fieldId) && kind === 'steam_plant') return { ok: false, msg: `${fdef.name} is mined, not steamed.` };
  if (stat(def, 'retrofit', false) && !unitsAt(state, fieldId, 'steam_plant').some((it) => !stat(eqDef(it.defId), 'retrofit', false))) return { ok: false, msg: 'Retrofit an existing SAGD central plant.' };
  if (kind === 'diluent' && unitsAt(state, fieldId, 'diluent').length) return { ok: false, msg: 'A diluent supply contract is already in place.' };
  if (!state.fields[fieldId].plots.some((p) => p.owned)) return { ok: false, msg: `Lease acreage at ${fdef.name} first.` };
  const blocked = regionBlock(state, fieldId);
  if (blocked) return { ok: false, msg: blocked };
  const price = unitPrice(state, def);
  if (price > 0 && !spend(state, price, 'capex')) return { ok: false, msg: `${def.name} costs ${fmt$(price)}.` };
  addItem(state, defId, { type: 'field', id: fieldId });
  if (fdef.oilSands) osState(state, fieldId); // register the field for the daily loop
  return { ok: true, msg: `${def.name} ${price > 0 ? `built at ${fdef.name} (${fmt$(price)})` : `signed for ${fdef.name}`}.` };
}

export function openMineBlockImpl(state, fieldId, plotIdx) {
  if (!m4On(state)) return { ok: false, msg: 'Not before 2000.' };
  if (!isMining(fieldId)) return { ok: false, msg: `${fieldDef(fieldId)?.name ?? fieldId} is not a mineable oil-sands lease.` };
  const p = state.fields[fieldId]?.plots?.[plotIdx];
  if (!p) return { ok: false, msg: 'No such plot.' };
  if (!p.owned) return { ok: false, msg: 'Lease the block first.' };
  if (p.wellId) return { ok: false, msg: 'There is a well on that plot.' };
  const res = reservoirsAt(fieldDef(fieldId), p);
  if (!res.length) return { ok: false, msg: 'Too much overburden here: no mineable ore under this block.' };
  const os = osState(state, fieldId);
  if (os.blocks[plotIdx]) return { ok: false, msg: 'That block is already being mined.' };
  const r = res[0];
  const rs = state.fields[fieldId].reservoirs[r.id];
  const nPlots = state.fields[fieldId].plots.filter((q) => reservoirsAt(fieldDef(fieldId), q).some((x) => x.id === r.id)).length || 1;
  const reserve = Math.round((rs?.initial ?? 0) / nPlots);
  os.blocks[plotIdx] = { resId: r.id, reserveBbl: reserve, minedBbl: 0, openDay: state.day };
  p.mine = { openDay: state.day };
  return { ok: true, msg: `Mine block opened: about ${(reserve / 1e6).toFixed(0)} million bbl of bitumen recoverable.` };
}

export function treatTailingsImpl(state, fieldId, amount = null) {
  const os = peekW(state)?.oilSands?.[fieldId];
  if (!os || !(os.liability > 0)) return { ok: false, msg: 'No untreated tailings liability.' };
  const pay = Math.min(os.liability, amount ?? os.liability, Math.max(0, state.company.cash));
  if (!(pay > 0)) return { ok: false, msg: 'No cash to fund tailings treatment.' };
  spend(state, pay, 'other', false);
  os.liability -= pay; os.treated += pay;
  return { ok: true, msg: `Paid ${fmt$(pay)} to dry and reclaim tailings; ${fmt$(os.liability)} of liability remains.` };
}

// ---- daily ----------------------------------------------------------------------------------------------------------------
const opsCrew = (state) => Object.values(state.crew ?? {}).filter((c) => c.status === 'active' && c.role === 'heavy_equipment_op').length;
const $ = (state, x, base) => eraDollars(state, x, base);

function mineDay(state, fid) {
  const os = osState(state, fid);
  const blocks = Object.values(os.blocks);
  os.today = { ore: 0, bitumen: 0, syncrude: 0, dilbit: 0, cost: 0 };
  if (!blocks.length) { os.status = 'NO_BLOCKS'; return; }
  if (wildfireShut(state, fid) || os.shutUntil > state.day) { os.status = 'WILDFIRE'; return; }
  const fleets = unitsAt(state, fid, 'mine_fleet');
  const extraction = unitsAt(state, fid, 'extraction');
  if (!fleets.length) { os.status = 'NO_FLEET'; return; }
  if (!extraction.length) { os.status = 'NO_EXTRACTION'; return; }
  const tailCap = sumStat(unitsAt(state, fid, 'tailings'), 'capacityBbl');
  if (!(tailCap > os.tailingsBbl)) { os.status = 'NO_TAILINGS'; return; }
  const cfg = osDef(fid);
  const crewF = 0.5 + 0.5 * Math.min(1, opsCrew(state) / (CREWS_PER_FLEET * fleets.length));
  const ore = sumStat(fleets, 'oreTpd') * crewF;
  const recovery = cfg.recovery ?? OIL_SANDS.mining.extractionRecovery;
  const left = blocks.reduce((a, b) => a + Math.max(0, b.reserveBbl - b.minedBbl), 0);
  let bitumen = Math.min(ore / (cfg.oreTonnesPerBbl ?? OIL_SANDS.mining.oreTonnesPerBbl) * recovery, sumStat(extraction, 'capacityBbld'), left);
  const upg = unitsAt(state, fid, 'upgrader');
  const froth = unitsAt(state, fid, 'froth');
  const diluent = unitsAt(state, fid, 'diluent').length > 0;
  const synIn = Math.min(bitumen, sumStat(upg, 'capacityBbld'));
  const dilIn = diluent ? Math.min(bitumen - synIn, sumStat(froth, 'capacityBbld')) : 0;
  bitumen = synIn + dilIn;
  if (!(bitumen > 0)) { os.status = left <= 0 ? 'MINED_OUT' : 'NO_OUTLET'; return; }
  os.status = 'RUNNING';
  const usedOre = bitumen * (cfg.oreTonnesPerBbl ?? OIL_SANDS.mining.oreTonnesPerBbl) / recovery;
  const upYield = upg.length ? stat(eqDef(upg[0].defId), 'yield', OIL_SANDS.mining.upgraderYield) : OIL_SANDS.mining.upgraderYield;
  const frYield = froth.length ? stat(eqDef(froth[0].defId), 'yield', 0.92) : 0.92;
  const syncrude = synIn * upYield;
  const dilbit = dilIn * frYield;
  // costs (2010 $ base unless the unit says otherwise)
  const P = oilSandsPrices(state);
  const fleetDef = eqDef(fleets[0].defId), exDef = eqDef(extraction[0].defId);
  let cost = usedOre * $(state, stat(fleetDef, 'opexPerT', 4), stat(fleetDef, 'baseYear', 2010)) + bitumen * $(state, stat(exDef, 'opexPerBbl', 6), stat(exDef, 'baseYear', 2010));
  if (synIn > 0) { const ud = eqDef(upg[0].defId); cost += synIn * ($(state, stat(ud, 'opexPerBbl', 10), stat(ud, 'baseYear', 2008)) + stat(ud, 'gasMcfPerBbl', 0.5) * P.aeco); }
  if (dilIn > 0 && froth.length) { const fd = eqDef(froth[0].defId); cost += dilIn * $(state, stat(fd, 'opexPerBbl', 3), stat(fd, 'baseYear', 2013)); }
  spend(state, cost, 'opex', false);
  // deplete blocks in order, and the reservoir
  let take = bitumen;
  for (const b of blocks) { const t = Math.min(take, Math.max(0, b.reserveBbl - b.minedBbl)); b.minedBbl += t; take -= t; const rs = state.fields[fid].reservoirs[b.resId]; if (rs) rs.remaining = Math.max(0, rs.remaining - t); if (take <= 0) break; }
  // tailings & liability
  const tailPer = cfg.tailingsPerBbl ?? OIL_SANDS.mining.tailingsPerBbl;
  os.tailingsBbl += bitumen * tailPer;
  const tDef = eqDef(unitsAt(state, fid, 'tailings')[0]?.defId);
  os.liability += bitumen * $(state, stat(tDef, 'liabilityPerBbl', OIL_SANDS.mining.reclamationLiabilityPerBbl), stat(tDef, 'baseYear', OIL_SANDS.mining.baseYear)) * tailPer;
  const stored = storeOil(state, fid, syncrude + dilbit);
  state.company.stats.bblProduced += stored;
  os.bitumenBbl += bitumen; os.syncrudeBbl += syncrude; os.dilbitBbl += dilbit;
  const share = syncrude + dilbit > 0 ? syncrude / (syncrude + dilbit) : 0;
  os.syncShare = os.syncShare ? os.syncShare * 0.9 + share * 0.1 : share;
  os.today = { ore: Math.round(usedOre), bitumen: Math.round(bitumen), syncrude: Math.round(syncrude), dilbit: Math.round(dilbit), cost: Math.round(cost) };
}

export function sagdSor(state, fid) {
  const base = osDef(fid)?.sor ?? OIL_SANDS.sagd.sorTypical;
  const solvent = unitsAt(state, fid, 'steam_plant').some((it) => stat(eqDef(it.defId), 'retrofit', false));
  return base * (solvent ? stat(eqDef('solvent_sagd_kit'), 'sorMult', 0.75) : 1) * (hasUpgrade(state, 'sagd_optimization') ? 0.85 : 1);
}

function sagdDay(state, fid) {
  const pairs = wellsOnField(state, fid).filter((w) => ownWell(w) && w.status === 'PRODUCING' && w.prod);
  const os = osState(state, fid);
  os.today = { pairs: pairs.length, bitumen: 0, steam: 0, gasMcf: 0, cost: 0 };
  if (!pairs.length) { os.status = 'NO_PAIRS'; return; }
  for (const w of pairs) { w.m4Loop = 'sagd'; w.sagd ??= { peakBbld: Math.round(w.prod.qi), steamStartDay: null }; }
  const shut = wildfireShut(state, fid);
  const plants = unitsAt(state, fid, 'steam_plant').filter((it) => !stat(eqDef(it.defId), 'retrofit', false));
  const steamCap = shut ? 0 : sumStat(plants, 'steamBbld');
  const bitCap = sumStat(plants, 'capacityBbld');
  const sor = sagdSor(state, fid);
  const pot = pairs.map((w) => {
    if (w.sagd.steamStartDay == null && steamCap > 0) w.sagd.steamStartDay = state.day;
    if (w.sagd.steamStartDay == null || w.prod.forcedUntil > state.day || w.prod.shutIn) return 0;
    return sagdRate(w.sagd.peakBbld, (state.day - w.sagd.steamStartDay) / 365.25);
  });
  const want = pot.reduce((a, b) => a + b, 0);
  const f = want > 0 ? Math.min(1, steamCap / (want * sor), bitCap / want) : 0;
  let total = 0;
  pairs.forEach((w, i) => {
    const res = state.fields[fid].reservoirs[w.reservoirId];
    const q = Math.max(0, Math.min(pot[i] * f, res?.remaining ?? 0));
    if (res) res.remaining -= q;
    w.prod.rate = q; w.prod.cum += q; w.prod.age += 1;
    if (!(steamCap > 0) && w.problem?.type !== 'NO_STEAM') w.problem = { type: 'NO_STEAM', sinceDay: state.day, text: shut ? 'shut in: wildfire evacuation.' : 'no steam: build a SAGD central plant (OTSG) at the field.' };
    else if (steamCap > 0 && w.problem?.type === 'NO_STEAM') w.problem = null;
    total += q;
  });
  os.status = shut ? 'WILDFIRE' : !(steamCap > 0) ? 'NO_STEAM' : f < 0.999 ? 'STEAM_LIMITED' : 'RUNNING';
  if (!(total > 0)) return;
  const P = oilSandsPrices(state);
  const steam = total * sor;
  const gasMcf = steam * (osDef(fid)?.gasMcfPerBblSteam ?? OIL_SANDS.sagd.gasMcfPerBblSteam);
  const pd = eqDef(plants[0]?.defId);
  const opex = total * $(state, stat(pd, 'opexPerBbl', 8), stat(pd, 'baseYear', 2010));
  spend(state, gasMcf * P.aeco, 'fuel', false);
  spend(state, opex, 'opex', false);
  const stored = storeOil(state, fid, total);
  state.company.stats.bblProduced += stored;
  os.bitumenBbl += total;
  os.today = { pairs: pairs.length, bitumen: Math.round(total), steam: Math.round(steam), gasMcf: Math.round(gasMcf), sor: Math.round(sor * 100) / 100, cost: Math.round(gasMcf * P.aeco + opex) };
  state.company.stats.sagdPairs = Math.max(state.company.stats.sagdPairs ?? 0, pairs.length);
}

export function tickOilSands(state) {
  const fids = new Set();
  const Wm = peekW(state);
  for (const fid of Object.keys(Wm?.oilSands ?? {})) fids.add(fid); // registered by buildOilSandsUnit / openMineBlock
  for (const fid of ['athabasca_sagd']) if (state.fields[fid] && wellsOnField(state, fid).some((w) => w.status === 'PRODUCING')) fids.add(fid);
  for (const fid of fids) {
    if (!state.fields[fid]) continue;
    if (isMining(fid)) mineDay(state, fid);
    else if (isSagd(fid)) sagdDay(state, fid);
  }
}

// Monthly: Directive 074 fines on untreated tailings (from 2009).
export function chargeTailingsFines(state) {
  const Wm = peekW(state);
  if (!Wm || yf(state) < 2009) return;
  for (const [fid, os] of Object.entries(Wm.oilSands)) {
    if (!(os.liability > 0)) continue;
    const fine = os.liability * D074_FINE_PER_MONTH;
    spend(state, fine, 'fines', false);
    os.fines += fine;
    if (!os.d074Warned) { os.d074Warned = state.day; notify(state, 'news', { kind: 'politics', fieldId: fid, level: 'warn', text: `Directive 074: Alberta fines you ${fmt$(fine)} a month for undried tailings at ${fieldDef(fid)?.name}. Fund treatment to stop the fines.` }); }
  }
}

// ---- sale hooks (production.js) -------------------------------------------------------------------------------------------
// Reason the field's crude cannot be sold (no diluent / upgrader for bitumen and extra-heavy oil), or null.
export function heavySaleBlock(state, fid) {
  if (!m4On(state)) return null;
  if (isSagd(fid) || isHeavy(fid)) {
    if (unitsAt(state, fid, 'diluent').length || unitsAt(state, fid, 'upgrader').length) return null;
    return `${fieldDef(fid)?.name}: bitumen and extra-heavy crude will not flow down a pipeline — sign a diluent supply contract or build an upgrader.`;
  }
  return null;
}
// Price multiplier on the field's quality (syncrude premium at the mines, upgraded Orinoco crude).
export function heavyPriceMult(state, fid) {
  if (isMining(fid)) {
    const share = peekW(state)?.oilSands?.[fid]?.syncShare ?? 0;
    if (!(share > 0)) return 1;
    const P = oilSandsPrices(state);
    return P.wcs > 0 ? 1 + share * (P.wti / P.wcs - 1) : 1;
  }
  if (isHeavy(fid) && unitsAt(state, fid, 'upgrader').length) return UPGRADER_PRICE_UPLIFT;
  return 1;
}
// After a sale of `bbl` delivered for `gross`: the diluent's net cost (dilbit fields without an upgrader).
export function heavyOnSale(state, fid, bbl, gross) {
  if (!(bbl > 0)) return 0;
  let dilShare = 0;
  if (isMining(fid)) dilShare = 1 - (peekW(state)?.oilSands?.[fid]?.syncShare ?? 0);
  else if ((isSagd(fid) || isHeavy(fid)) && !unitsAt(state, fid, 'upgrader').length) dilShare = 1;
  if (!(dilShare > 0)) {
    if (isHeavy(fid)) { const up = unitsAt(state, fid, 'upgrader')[0]; if (up) { const c = bbl * $(state, stat(eqDef(up.defId), 'opexPerBbl', 10), stat(eqDef(up.defId), 'baseYear', 2008)); spend(state, c, 'opex', false); return c; } }
    return 0;
  }
  const P = oilSandsPrices(state);
  const blend = isHeavy(fid) ? (fieldDef(fid).heavyOil.diluentFrac ?? HEAVY_OIL.diluentFrac) : OIL_SANDS.blending.dilbitDiluentFrac;
  const dilDef = eqDef('diluent_supply');
  const cost = bbl * dilShare * diluentNetCost(blend, P.diluent * stat(dilDef, 'priceMult', 1), gross / bbl);
  if (cost > 0) spend(state, cost, 'consumables', false);
  return cost;
}

// ---- view -----------------------------------------------------------------------------------------------------------------
export function oilSandsView(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef || !(fdef.oilSands || fdef.heavyOil)) return null;
  const os = peekW(state)?.oilSands?.[fieldId] ?? null;
  const units = itemsAt(state, fieldId, (it, d) => d?.cat === 'oil_sands').map((it) => ({ uid: it.uid, defId: it.defId, name: eqDef(it.defId)?.name, kind: stat(eqDef(it.defId), 'kind', '') }));
  const on = m4On(state);
  return {
    fieldId, extraction: fdef.oilSands?.extraction ?? (fdef.heavyOil ? 'extra_heavy' : null), active: on, status: os?.status ?? null, units,
    blocks: Object.entries(os?.blocks ?? {}).map(([plot, b]) => ({ plot: +plot, ...b, leftBbl: Math.max(0, b.reserveBbl - b.minedBbl) })),
    tailingsBbl: Math.round(os?.tailingsBbl ?? 0), liability: Math.round(os?.liability ?? 0), treated: Math.round(os?.treated ?? 0), finesPaid: Math.round(os?.fines ?? 0),
    produced: { bitumen: Math.round(os?.bitumenBbl ?? 0), syncrude: Math.round(os?.syncrudeBbl ?? 0), dilbit: Math.round(os?.dilbitBbl ?? 0) }, today: os?.today ? { ...os.today } : null,
    sor: on && fdef.oilSands?.extraction === 'sagd' ? Math.round(sagdSor(state, fieldId) * 100) / 100 : null,
    prices: on ? oilSandsPrices(state) : null, saleBlocked: on ? heavySaleBlock(state, fieldId) : null,
    buildable: ['mine_fleet', 'hot_water_extraction', 'paraffinic_froth_treatment', 'bitumen_upgrader', 'otsg_steam_plant', 'solvent_sagd_kit', 'diluent_supply', 'tailings_facility']
      .filter((id) => eqDef(id) && (!on ? false : isAvailable(eqDef(id), state))),
  };
}
